// Product brief for Hybrid Life IRA - OneAmerica Asset Care III. See cmTwo.ts
// for the format intent.

export const hybridLifeIraBrief = `**Hybrid Life IRA** — OneAmerica Asset Care III (IRA-funded asset-based hybrid).

⚠️ **Ground truth — UNVERIFIED MODELING.** There is **NO carrier illustration**
in this repo for Asset Care III. Every mechanic below
mirrors the legacy aggregate calc (\`calcFundingSourceDerivatives.ts\`,
\`hybridLifeIra\` branches) exactly — bit-equal at 0% inflation. These policy
types are flagged \`hasUnverifiedModeling: true\`. Treat all figures as the
legacy model's output, not as carrier-confirmed values. Do NOT invent numbers
from a non-existent illustration.

Pool model — two pools, drawn in order:
- **Face / Acceleration of Benefits (base)** is the \`base_pool\`, drawn FIRST.
- **Continuation of Benefits (COB)** is the \`eob\` column, drawn only after the
  base pool is exhausted.
- Each pool inflates independently at the policy anniversary on its own rate
  (\`base_pool_inflation\` / \`eob_pool_inflation\`), gated on the pool remaining
  open. Growth is compound by default; simple riders
  (\`isSimpleInflationProtection\`, surfaced as \`inflation_rate … simple\` in the
  header) grow linearly by a fixed at-issue step per anniversary (P2-38).

Payout model — withdrawal-rate base + COB rider:
- **Base daily** is NOT a fixed daily benefit. It is derived from a monthly
  withdrawal rate applied to the inflated FULL face:
  \`baseDaily = inflatedFace × monthlyWithdrawalRate / 30\`. Reimbursement caps
  it at the actual daily cost; indemnity leaves it uncapped.
- **COB/rider daily** mirrors legacy: the inflated per-environment daily benefit
  (on the COB rate). These products typically carry NO per-environment daily
  benefit, so the COB daily resolves to **$0** and the COB pool pays nothing —
  this is the legacy behavior reproduced faithfully, and the main unverified gap
  (a real Asset Care III COB may pay differently; we cannot confirm).
- **Missing vs. explicit-0 daily:** a *missing* daily benefit is expected (these
  products quote a withdrawal rate) and the withdrawal-rate base path still
  fires. An *explicit 0* daily for the active environment hard-zeroes the row.
- **Indemnity:** uncapped daily-rate × eligible days as cash, regardless of
  cost. It does NOT apply any \`policyIndemnityBenefitPercent\` multiplier
  (verified against legacy) — unlike Asset Care's indemnity-percent model.

Death benefit:
- Flat Face Amount, depleted **dollar-for-dollar by base-pool drawdown only**.
  COB drawdown does NOT reduce the death benefit. Floored at the minimum
  guaranteed amount (typically 0 for these products).

Elimination period:
- Two-tier EP (home vs. facility), served via unpaid waiting days. **No
  retroactive WP lump** — EP days are fully out-of-pocket (\`in-wp\` rows).

Things to eyeball:
- Base depletes before any COB drawdown — \`eob\` stays flat until \`base_pool\` hits 0.
- COB typically pays $0 (no per-environment daily benefit) — confirm whether
  that matches reality once a carrier illustration is available.
- Base monthly cap grows as \`inflatedFace × withdrawalRate\` year over year.
- Death benefit falls in step with base drawdown, not COB.
- No EP reimbursement lump anywhere.
`;
